// The seeds behind every built-in theme.
//
// These are the source of truth for styles/themes.css, which is *generated* —
// `npm run gen:themes` runs deriveVars over each palette below and writes the
// stylesheet. Edit a colour here and regenerate; editing themes.css directly is
// undone by the next run.
//
// Why generate rather than hand-write the CSS, as this project did until now:
// built-ins and member-built palettes now go through the same derivation, so a
// palette designed in the app's own editor becomes a built-in unchanged, and an
// improvement to deriveVars improves every shipped theme instead of only the
// custom ones. It also removes the standing risk in a hand-maintained file of
// twelve near-identical blocks — that one of them quietly omits a variable and
// inherits Blueberry's.
//
// The seed format is exactly the app's theme export (lib/themeShare.ts), so a
// palette can go editor -> Export -> this file with no translation.

import type { Palette } from "../api/types";

export interface BuiltinPalette {
  /** The `data-theme` value. Stable — a member's stored choice is this string. */
  id: string;
  name: string;
  /** One line of kid-readable flavour, shown under the swatch. */
  blurb: string;
  palette: Palette;
}

/**
 * Order matters twice: it is the order the picker shows, and the order the
 * generated stylesheet declares. Blueberry must stay first — it doubles as
 * `:root`, so an unthemed document still looks like the app.
 */
export const BUILTIN_PALETTES: BuiltinPalette[] = [
  {
    id: "default",
    name: "Blueberry",
    blurb: "Cool and classic",
    palette: {
      light: {
        brand: "#7f84b8",
        spend: "#61689e",
        save: "#414986",
        celebrate: "#86d08e",
        surface: "#e5e7f5",
        text: "#3f4983",
        background: {
          angle: 160,
          stops: [
            { color: "#f8fdf7", position: 0 },
            { color: "#cec4e3", position: 100 },
          ],
        },
      },
      dark: {
        brand: "#7f84b8",
        spend: "#61689e",
        save: "#414986",
        celebrate: "#86d08e",
        surface: "#262353",
        text: "#d8dbee",
        background: {
          angle: 160,
          stops: [
            { color: "#2e4e5c", position: 0 },
            { color: "#1d0849", position: 100 },
          ],
        },
      },
    },
  },
  {
    id: "bubblegum",
    name: "Bubblegum",
    blurb: "Bright and pink",
    palette: {
      light: {
        brand: "#c341ab",
        spend: "#d36fae",
        save: "#f39b9b",
        celebrate: "#9cc115",
        surface: "#fff0f6",
        text: "#2c2848",
        background: {
          angle: 160,
          stops: [
            { color: "#ffe6cc", position: 0 },
            { color: "#fbd2fe", position: 100 },
          ],
        },
      },
      dark: {
        brand: "#c341ab",
        spend: "#d36fae",
        save: "#f39b9b",
        celebrate: "#aac913",
        surface: "#241a32",
        text: "#7989b9",
        background: {
          angle: 160,
          stops: [
            { color: "#454e78", position: 29 },
            { color: "#1b1f33", position: 100 },
          ],
        },
      },
    },
  },
  {
    id: "sunset",
    name: "Sunset",
    blurb: "Warm and glowing",
    palette: {
      light: {
        brand: "#2b94b3",
        spend: "#f3403a",
        save: "#fcab10",
        celebrate: "#ee4a39",
        surface: "#ffffff",
        text: "#1a1f2e",
        background: {
          angle: 180,
          stops: [
            { color: "#f4efe5", position: 0 },
            { color: "#fee9c0", position: 31 },
            { color: "#ff8591", position: 100 },
          ],
        },
      },
      dark: {
        brand: "#f8333c",
        spend: "#fcab10",
        save: "#2b94b3",
        celebrate: "#389c90",
        surface: "#322d43",
        text: "#f38144",
        background: {
          angle: 180,
          stops: [
            { color: "#d89b22", position: 0 },
            { color: "#d56801", position: 31 },
            { color: "#b50d1d", position: 100 },
          ],
        },
      },
    },
  },
  {
    id: "dusk",
    name: "Dusk",
    blurb: "Purple and dreamy",
    palette: {
      light: {
        brand: "#d264b6",
        spend: "#779be7",
        save: "#a480cf",
        celebrate: "#cd68b2",
        surface: "#e3e3f8",
        text: "#2a3f7e",
        background: {
          angle: 180,
          stops: [
            { color: "#becff4", position: 0 },
            { color: "#cdbae6", position: 100 },
          ],
        },
      },
      dark: {
        brand: "#d264b6",
        spend: "#779be7",
        save: "#a480cf",
        celebrate: "#cd68b2",
        surface: "#283f7b",
        text: "#8b9bcb",
        background: {
          angle: 180,
          stops: [
            { color: "#0d3282", position: 0 },
            { color: "#5a2f93", position: 100 },
          ],
        },
      },
    },
  },
  {
    id: "earth",
    name: "Earth",
    blurb: "Fresh and outdoorsy",
    palette: {
      light: {
        brand: "#5b4b49",
        spend: "#23967f",
        save: "#7daf9c",
        celebrate: "#1652a2",
        surface: "#ffffff",
        text: "#1a1f2e",
        background: {
          angle: 180,
          stops: [
            { color: "#b2d0e6", position: 22 },
            { color: "#91cab5", position: 82 },
          ],
        },
      },
      dark: {
        brand: "#5b4b49",
        spend: "#23967f",
        save: "#7daf9c",
        celebrate: "#1652a2",
        surface: "#191a1a",
        text: "#5b8395",
        background: {
          angle: 180,
          stops: [
            { color: "#284458", position: 22 },
            { color: "#124935", position: 82 },
          ],
        },
      },
    },
  },
  {
    id: "river",
    name: "River",
    blurb: "Calm and flowing",
    palette: {
      light: {
        brand: "#88b497",
        spend: "#cab886",
        save: "#f89b5d",
        celebrate: "#52b9c7",
        surface: "#f7f8fd",
        text: "#44568d",
        background: {
          angle: 160,
          stops: [
            { color: "#f6f7fb", position: 0 },
            { color: "#a3a5b8", position: 100 },
          ],
        },
      },
      dark: {
        brand: "#629d79",
        spend: "#b09d69",
        save: "#dc8850",
        celebrate: "#52b9c7",
        surface: "#242a47",
        text: "#e4e8f1",
        background: {
          angle: 160,
          stops: [
            { color: "#666d8a", position: 0 },
            { color: "#0a0d1f", position: 100 },
          ],
        },
      },
    },
  },
  {
    id: "rainbow",
    name: "Rainbow",
    blurb: "Every colour at once",
    palette: {
      light: {
        brand: "#7dc2ed",
        spend: "#e4dd53",
        save: "#f18e9b",
        celebrate: "#d35fc4",
        surface: "#f8f5f2",
        text: "#241c97",
        background: {
          angle: 120,
          stops: [
            { color: "#8a89d2", position: 16 },
            { color: "#79c4ec", position: 30 },
            { color: "#abd09a", position: 43 },
            { color: "#dfdb68", position: 56 },
            { color: "#f0ae84", position: 72 },
            { color: "#e58fa8", position: 88 },
          ],
        },
      },
      dark: {
        brand: "#254f6a",
        spend: "#9f9b50",
        save: "#905159",
        celebrate: "#8f2481",
        surface: "#0f0326",
        text: "#37e683",
        background: {
          angle: 120,
          stops: [
            { color: "#343380", position: 16 },
            { color: "#1c6287", position: 30 },
            { color: "#385e26", position: 43 },
            { color: "#999529", position: 56 },
            { color: "#924f26", position: 72 },
            { color: "#9f2d4d", position: 88 },
          ],
        },
      },
    },
  },
];
